import { z } from 'zod';
import { approvePromptRequests } from '../utils/generation-billing.svelte.js';
import { recoverGenerationFailure } from '../state/credit-recovery.svelte.js';
import { requireSignedInClient } from '../utils/require-sign-in.js';
import { studioSettings } from '../state/settings.svelte.js';
import {
  STRUCTURED_ATTEMPT_LABELS,
  STRUCTURED_OUTPUT_TOOL,
  buildDescribeImageRequest,
  buildStructuredRequests
} from './structured-requests.js';
import {
  LLM_TIMEOUT_MESSAGE,
  assertImageDataUrl,
  canStartAttempt,
  createDeadline,
  remainingMs,
  toFriendlyLLMError
} from './policy.js';

/**
 * Browser LLM client.
 *
 * Every LLM call runs on the signed-in user's own Sogni account through the
 * browser SDK singleton (`client.chat.completions.create`). The Vibe server
 * holds no LLM path at all, so a logged-out visitor has nothing to spend.
 * There is deliberately no guest or server fallback: signed out means the
 * request fails and the sign-in modal opens.
 */

export const DEFAULT_LLM_MODEL = import.meta.env.VITE_SOGNI_LLM_MODEL || 'qwen3.6-35b-a3b-gguf-iq4xs';
const MODELS_WAIT_MS = 10000;

export const SIGN_IN_REQUIRED_MESSAGE = 'Sign in to use AI prompt tools — they run on your own Sogni account.';

/**
 * Return the signed-in user's SDK client, or open the sign-in modal and throw.
 */
export function requireLLMClient() {
  return requireSignedInClient(SIGN_IN_REQUIRED_MESSAGE);
}

/**
 * Keep billing context and open recovery without making a detached retry.
 */
function finalizeLLMError(error) {
  const friendly = toFriendlyLLMError(error);
  recoverGenerationFailure(friendly, { kind: 'prompt', request: friendly.request, quote: friendly.quote });
  return friendly;
}

async function resolveModel(client, requestedModel, deadlineAt) {
  const model = requestedModel || DEFAULT_LLM_MODEL;
  let available = client.chat.models;
  if (!available || Object.keys(available).length === 0) {
    available = await client.chat.waitForModels(Math.min(MODELS_WAIT_MS, remainingMs(deadlineAt)));
  }

  if (!available || !(model in available)) {
    throw new Error('The AI prompt model is offline right now. Please try again shortly.');
  }

  return model;
}

function withTimeout(promise, timeoutMs, timeoutMessage) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timer));
}

function extractBalancedJsonObject(text) {
  if (typeof text !== 'string') return null;
  let start = -1;
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (start === -1) {
      if (ch === '{') {
        start = i;
        depth = 1;
      }
      continue;
    }

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{') depth += 1;
    if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        return text.slice(start, i + 1);
      }
    }
  }

  return null;
}

export function parseJsonFromModelContent(rawContent) {
  const cleaned = String(rawContent ?? '')
    .replace(/<\|im_end\|>\s*$/i, '')
    .trim();

  const withoutThinkBlocks = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
  const withoutThinkMarkers = withoutThinkBlocks
    .replace(/<\/?think>/gi, '')
    .trim();
  const danglingThinkTrimmed = withoutThinkBlocks
    .replace(/<think>[\s\S]*$/i, '')
    .trim();
  const fromFirstBrace = (() => {
    const braceIndex = withoutThinkMarkers.indexOf('{');
    return braceIndex >= 0 ? withoutThinkMarkers.slice(braceIndex).trim() : null;
  })();
  const fencedMatch = withoutThinkBlocks.match(/```(?:json)?\s*([\s\S]*?)```/i);

  const candidates = [
    cleaned,
    withoutThinkBlocks,
    withoutThinkMarkers,
    danglingThinkTrimmed,
    fromFirstBrace,
    fencedMatch?.[1]?.trim(),
    extractBalancedJsonObject(withoutThinkBlocks),
    extractBalancedJsonObject(danglingThinkTrimmed),
    extractBalancedJsonObject(fromFirstBrace)
  ].filter(Boolean);

  let lastError;
  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(`Unable to parse JSON from model content: ${lastError?.message || 'no parse candidates worked'}`);
}

function parseToolArguments(rawArguments) {
  if (!rawArguments) return null;
  if (typeof rawArguments === 'object') return rawArguments;
  if (typeof rawArguments !== 'string') return null;

  try {
    return JSON.parse(rawArguments);
  } catch {
    return parseJsonFromModelContent(rawArguments);
  }
}

function extractStructuredFromToolCalls(toolCalls) {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return null;

  const preferredCall = toolCalls.find(
    (toolCall) => toolCall?.function?.name === STRUCTURED_OUTPUT_TOOL
  ) || toolCalls[0];

  return parseToolArguments(preferredCall?.function?.arguments);
}

function unwrapToolLikePayload(value) {
  if (!value) return value;

  if (Array.isArray(value)) {
    const firstToolEntry = value.find((entry) =>
      entry
      && typeof entry === 'object'
      && ('arguments' in entry || ('function_call' in entry && entry.function_call))
    );

    if (firstToolEntry) {
      if ('arguments' in firstToolEntry && firstToolEntry.arguments) {
        return firstToolEntry.arguments;
      }

      if ('function_call' in firstToolEntry && firstToolEntry.function_call?.arguments) {
        return firstToolEntry.function_call.arguments;
      }
    }

    return value[0] ?? value;
  }

  if (typeof value === 'object') {
    if ('arguments' in value && value.arguments) {
      return value.arguments;
    }

    if ('function_call' in value && value.function_call?.arguments) {
      return value.function_call.arguments;
    }
  }

  return value;
}

function normalizeMessages(messages) {
  if (!Array.isArray(messages)) return [];

  return messages
    .filter((msg) => msg && typeof msg.content === 'string')
    .map((msg) => {
      const role = msg.role === 'system' || msg.role === 'assistant' ? msg.role : 'user';
      return { role, content: msg.content };
    });
}

/**
 * Convert a zod object schema into the JSON schema sent as the structured
 * output tool's parameters.
 */
export function zodToJsonSchema(schema) {
  const { $schema, ...jsonSchema } = z.toJSONSchema(schema);
  const objectSchema = jsonSchema.type === 'object'
    ? jsonSchema
    : jsonSchema.anyOf?.find((option) => option?.type === 'object');

  if (!objectSchema) {
    throw new Error('Structured output schema must describe an object');
  }

  return {
    ...objectSchema,
    properties: objectSchema.properties || {},
    required: Array.isArray(objectSchema.required) ? objectSchema.required : [],
    additionalProperties: objectSchema.additionalProperties ?? false
  };
}

/**
 * Ask the model for one JSON object matching `schema`, on the signed-in
 * user's account. Tries a forced tool call, then `tool_choice: required`, then
 * a JSON-only reply; thinking is disabled on every attempt.
 */
export async function generateStructuredObject(params) {
  try {
    return await runStructuredLadder(params);
  } catch (error) {
    throw finalizeLLMError(error);
  }
}

// One deadline covers the model wait and every rung; no rung starts once the
// budget is spent. The SDK cannot cancel a socket chat job, so a rung that
// times out is abandoned (its result, if any, is ignored).
async function runStructuredLadder(params) {
  const client = requireLLMClient();
  const accountId = client.account.currentAccount.walletAddress?.toLowerCase();

  const {
    model,
    messages,
    schema,
    temperature = 0.8,
    maxTokens,
    deadlineAt: originalDeadline = createDeadline()
  } = params;
  let deadlineAt = originalDeadline;

  const normalizedMessages = normalizeMessages(messages);
  if (normalizedMessages.length === 0) {
    throw new Error('generateStructuredObject requires at least one message with text content');
  }

  const effectiveModel = await resolveModel(client, model, deadlineAt);
  const jsonSchema = zodToJsonSchema(params.baseSchema || schema);
  // Built after the billing context is read, so the Mature Content flag in the
  // requests matches the source fingerprint frozen beside them.
  const approved = await approvePromptRequests(client, () => buildStructuredRequests({
    model: effectiveModel, messages: normalizedMessages, jsonSchema, temperature, maxTokens,
    matureContentEnabled: studioSettings.matureContentEnabled === true
  }), { accountId });
  deadlineAt += approved.reviewTimeMs;

  let lastError;

  for (let attempt = 1; attempt <= approved.requests.length; attempt += 1) {
    const label = STRUCTURED_ATTEMPT_LABELS[attempt - 1];

    if (!canStartAttempt(deadlineAt)) {
      break;
    }

    try {
      await approved.assertCurrent();
      const request = approved.requests[attempt - 1];

      const completion = await withTimeout(
        client.chat.completions.create(request),
        remainingMs(deadlineAt),
        LLM_TIMEOUT_MESSAGE
      );

      console.log(
        `[LLM][${label}] finishReason=${completion?.finishReason || 'unknown'} ` +
        `toolCalls=${completion?.tool_calls?.length || 0} contentChars=${(completion?.content || '').length} ` +
        `worker=${completion?.workerName || 'unknown'} jobID=${completion?.jobID || 'unknown'}`
      );

      let rawObject = extractStructuredFromToolCalls(completion?.tool_calls);

      if (rawObject == null && completion?.content) {
        try {
          rawObject = parseJsonFromModelContent(completion.content);
        } catch (parseError) {
          if (completion.finishReason === 'length') {
            throw new Error('Model output was truncated before the structured payload');
          }
          throw parseError;
        }
      }

      if (rawObject == null) {
        throw new Error('No structured output returned from tool call or content');
      }

      rawObject = unwrapToolLikePayload(rawObject);

      if (schema?.safeParse) {
        const parsed = schema.safeParse(rawObject);
        if (!parsed.success) {
          throw new Error(`Schema validation failed: ${parsed.error.issues.map((i) => i.message).join('; ')}`);
        }
        return parsed.data;
      }

      return rawObject;
    } catch (error) {
      // Account, balance and connection failures will not improve on the next
      // rung of the ladder — surface them immediately.
      if (!isRetryableStructuredError(error)) {
        error.request ||= approved.request;
        if (approved.quote) error.quote ||= approved.quote;
        throw error;
      }

      lastError = error;
      if (attempt < approved.requests.length) {
        console.warn(`⚠️ Structured output retry ${attempt}/${approved.requests.length} due to: ${error.message}`);
      }
    }
  }

  // Out of budget before the ladder produced a result.
  if (lastError && !canStartAttempt(deadlineAt)) {
    const timeoutError = new Error(LLM_TIMEOUT_MESSAGE);
    timeoutError.cause = lastError;
    throw timeoutError;
  }
  throw lastError || new Error(LLM_TIMEOUT_MESSAGE);
}

function isRetryableStructuredError(error) {
  const message = String(error?.message || '');
  return message.startsWith('Unable to parse JSON')
    || message.startsWith('Model output was truncated')
    || message.startsWith('No structured output returned')
    || message.startsWith('Schema validation failed')
    || message.startsWith('Structured output');
}

export function stripThinkBlocks(rawContent) {
  return String(rawContent ?? '')
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .replace(/<think>[\s\S]*$/i, '')
    .replace(/<\/?think>/gi, '')
    .replace(/<\|im_end\|>\s*$/i, '')
    .trim();
}

/**
 * Vision path: a plain text completion describing the image as a prompt.
 */
export async function describeImage(imageDataUrl, { model } = {}) {
  assertImageDataUrl(imageDataUrl);

  try {
    return await runDescribeImage(imageDataUrl, model);
  } catch (error) {
    throw finalizeLLMError(error);
  }
}

async function runDescribeImage(imageDataUrl, model) {
  const client = requireLLMClient();
  const accountId = client.account.currentAccount.walletAddress?.toLowerCase();
  let deadlineAt = createDeadline();
  const effectiveModel = await resolveModel(client, model, deadlineAt);
  // Describe-image's fixed user text is never rerouted by the socket, so no reroute ceiling.
  const approved = await approvePromptRequests(client, () => [buildDescribeImageRequest({
    model: effectiveModel, imageDataUrl, matureContentEnabled: studioSettings.matureContentEnabled === true
  })], { accountId, rerouteCeiling: false });
  deadlineAt += approved.reviewTimeMs;
  let completion;
  try {
    await approved.assertCurrent();
    completion = await withTimeout(client.chat.completions.create(approved.requests[0]), remainingMs(deadlineAt), LLM_TIMEOUT_MESSAGE);
  } catch (error) {
    error.request ||= approved.request;
    if (approved.quote) error.quote ||= approved.quote;
    throw error;
  }

  const description = stripThinkBlocks(completion?.content);
  if (!description) {
    if (completion?.finishReason === 'length') {
      throw new Error('Vision model output was truncated before producing a description');
    }
    throw new Error('Vision model returned an empty description');
  }

  return description;
}
